/**
 * Persistent tier + meter chip — visible from every authenticated surface
 * (mounted in AppShell). Two compact pieces of information:
 *
 *   1. Tier label: "Free" / "Premium" (Unlimited subscribers see no chip
 *      because there's no meter to render — see below).
 *   2. Radial fill that signals "how close are you to a wall" without
 *      leaking raw counts. The exact `used / limit` is deliberately
 *      hidden so users can't reverse-engineer per-action LLM cost by
 *      watching the chip tick. Approximate progress (fill arc + color
 *      ramp) is enough to make "uh oh approaching" obvious.
 *
 * The meter LABEL ("Daily" / "Monthly" / "First report") only renders
 * in the sidebar variant — the header variant on mobile drops it
 * to keep the chip compact in the tight space next to the brand mark.
 * Hover/tap target + tooltip still convey the full state.
 *
 * Variants per gating window:
 *
 * | State                          | Tier      | Meter        | Color ramp                  |
 * |--------------------------------|-----------|--------------|-----------------------------|
 * | Free, < 25 signals             | Free      | First report | Accent fill, positive       |
 * | Free, >= 25 signals (daily cap)| Free      | Daily        | Default → amber → red       |
 * | Standard subscription          | Premium   | Monthly      | Default → amber → red       |
 * | Unlimited subscription         | (hidden — no chip rendered)                            |
 *
 * Tap target:
 *   - Pre-25 progress → /skills (where their first-leak-report unlock lives)
 *   - Paying subscriber → /settings (plan + monthly allotment + manage)
 *   - Free / walled non-subscriber → /pricing (upsell)
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import {
  type BillingStatus,
  getBillingStatus,
  isNearLimit,
  isOverLimit,
  PLAN_LABELS,
} from "@/features/billing/lib/billing";

export type UsageChipVariant = "sidebar" | "header";

/**
 * - `sidebar` (default): nav-item-shaped, `w-full` inside the desktop
 *    side nav so it visually rhymes with the NavLink rows above.
 * - `header`: compact pill for the mobile header strip — content-sized.
 */
export function UsageChip({
  variant = "sidebar",
}: {
  variant?: UsageChipVariant;
} = {}) {
  const { data: status } = useQuery({
    queryKey: ["billing-status"],
    queryFn: getBillingStatus,
    // The chip is invalidated by ChatView after each turn (see
    // components/chat/ChatView.tsx). Pin staleTime so we don't
    // refetch on every nav focus event.
    staleTime: 60_000,
  });

  if (!status) return null;

  // Unlimited subscribers — no meter applies, hide.
  if (isUnlimitedSubscriber(status)) return null;

  const tier = tierLabel(status);
  const meter = meterLabel(status);
  const pct = meterPercent(status);
  const progress = isProgressVariant(status);
  const over = isOverLimit(status);
  const near = isNearLimit(status);

  // Color: pre-25 progress chip uses positive accent.
  // Capped windows shift muted → amber → red as user approaches/passes the wall.
  const colorClass = progress
    ? "text-accent"
    : over
      ? "text-danger"
      : near
        ? "text-amber-300"
        : "text-text-muted";

  const borderClass = progress
    ? "border-accent/30 hover:border-accent/60"
    : over
      ? "border-danger/40"
      : near
        ? "border-amber-500/40"
        : "border-border hover:border-accent/60";

  const bgClass = over
    ? "bg-danger/10"
    : near && !progress
      ? "bg-amber-500/10"
      : "bg-bg/40";

  // Tap target: pre-report progress → /skills (where the unlock lives);
  // a paying subscriber → /settings (their plan + monthly allotment +
  // Manage subscription); a free/walled non-subscriber → /pricing (upsell).
  const linkTo = progress
    ? "/skills"
    : isPaidSubscriber(status)
      ? "/settings"
      : "/pricing";

  // Title / aria-label deliberately omits raw counts. "Near limit" /
  // "Limit reached" gives the user the qualitative signal without
  // leaking how many responses cost what.
  const proximity = over
    ? "limit reached"
    : near
      ? "near limit"
      : progress
        ? "in progress"
        : "ok";
  const ariaLabel = `${tier} tier: ${meter} (${proximity})`;

  const layoutClass =
    variant === "sidebar"
      ? "flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm"
      : "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px]";

  const dialSize = variant === "sidebar" ? 18 : 14;

  return (
    <Link
      to={linkTo}
      title={ariaLabel}
      aria-label={ariaLabel}
      className={[
        layoutClass,
        "border transition",
        borderClass,
        bgClass,
      ].join(" ")}
    >
      <RadialFill percent={pct} colorClass={colorClass} size={dialSize} />
      <span className="truncate">
        <span className="text-text font-medium">{tier}</span>
        {variant === "sidebar" && (
          <span className="text-text-muted"> · {meter}</span>
        )}
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Pure derivations over BillingStatus
// ---------------------------------------------------------------------------

function isUnlimitedSubscriber(s: BillingStatus): boolean {
  return (
    (s.subscriptionStatus === "active" || s.subscriptionStatus === "past_due") &&
    s.planLevel === "unlimited"
  );
}

function isPaidSubscriber(s: BillingStatus): boolean {
  return s.subscriptionStatus === "active" || s.subscriptionStatus === "past_due";
}

function tierLabel(s: BillingStatus): string {
  if (isPaidSubscriber(s) && s.planLevel) {
    return PLAN_LABELS[s.planLevel];
  }
  return "Free";
}

function meterLabel(s: BillingStatus): string {
  if (s.gateType === "hard_wall") return "Locked";
  if (s.gatingWindow === "daily") return "Daily";
  if (s.gatingWindow === "monthly") return "Monthly";
  // gatingWindow === "none" + free + pre-25 signals.
  return "First report";
}

function isProgressVariant(s: BillingStatus): boolean {
  // Pre-first-report free is a positive fill toward unlocking the first
  // leak report — not a depleting cap. The hard wall (post-report) is
  // NOT progress: it's a depleted, locked state.
  return (
    s.gatingWindow === "none" && !isPaidSubscriber(s) && s.gateType !== "hard_wall"
  );
}

function meterPercent(s: BillingStatus): number {
  // Hard wall: a full, maxed dial (rendered red via `over`).
  if (s.gateType === "hard_wall") return 100;
  if (isProgressVariant(s)) {
    if (s.signalsForFirstReport <= 0) return 0;
    return clamp01(s.signalCount / s.signalsForFirstReport) * 100;
  }
  if (s.limit == null || s.limit <= 0) return 0;
  return clamp01(s.used / s.limit) * 100;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

// ---------------------------------------------------------------------------
// Radial fill — small SVG donut with an arc proportional to `percent`.
// ---------------------------------------------------------------------------

function RadialFill({
  percent,
  colorClass,
  size = 14,
}: {
  percent: number;
  colorClass: string;
  size?: number;
}) {
  const stroke = size >= 18 ? 2.5 : 2.25;
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const filled = (clampPercent(percent) / 100) * circumference;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={colorClass}
      aria-hidden
    >
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.22}
        strokeWidth={stroke}
      />
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeDasharray={`${filled} ${circumference}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${c} ${c})`}
      />
    </svg>
  );
}

function clampPercent(n: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 100) return 100;
  return n;
}
